import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Activity,
  User,
  MoveDiagonal,
  Zap,
  Dumbbell,
  Brain,
  HeartHandshake,
  Wind,
  Stethoscope,
  Leaf,
  Cpu,
  Award,
  ShieldCheck,
  Clock,
  PackageCheck,
  Quote,
  MessageCircle,
  PhoneCall,
  BookOpen,
} from "lucide-react";
import {
  BOOKING_URL,
  ASSETS,
  WHAT_WE_DO,
  CONDITIONS,
  PROCESS_STEPS,
  THERAPISTS,
  THERABODY_PRODUCTS,
  SEED_TESTIMONIALS,
  SESSIONS_DELIVERED,
} from "@/lib/constants";
import { fetchTestimonials, fetchGoogleReviews } from "@/lib/api";
import { absoluteUrl } from "@/lib/seo-config";
import TestimonialModal from "@/components/TestimonialModal";
import TrustCounter from "@/components/TrustCounter";
import SEO from "@/components/SEO";

const iconMap = {
  Activity,
  User,
  MoveDiagonal,
  Zap,
  Dumbbell,
  Brain,
  HeartHandshake,
  Wind,
  Stethoscope,
  Leaf,
  Cpu,
};

export default function Home() {
  const [testimonials, setTestimonials] = useState(SEED_TESTIMONIALS);

  useEffect(() => {
    let cancelled = false;
    fetchTestimonials()
      .then((rows) => {
        if (cancelled) return;
        if (Array.isArray(rows) && rows.length > 0) {
          // merge approved testimonials in front of seeds
          setTestimonials((prev) => [...rows, ...prev]);
        }
      })
      .catch((err) => {
        console.warn("fetchTestimonials failed (using seeded testimonials):", err);
      });
    // Pull cached Google reviews — instant from MongoDB cache, refreshed by
    // a background poller (Outscraper, 12h cadence).
    fetchGoogleReviews()
      .then((res) => {
        if (cancelled) return;
        const reviews = (res && Array.isArray(res.reviews)) ? res.reviews : [];
        if (reviews.length === 0) return;
        const mapped = reviews.slice(0, 3).map((r) => ({
          text: r.text,
          name: r.author_name || "Google reviewer",
          role: "Verified Google review",
          rating: r.rating,
          source: "google",
          author_image: r.author_image,
        }));
        setTestimonials((prev) => [...mapped, ...prev]);
      })
      .catch((err) => {
        console.warn("fetchGoogleReviews failed (using seeded testimonials):", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main data-testid="home-page" className="bg-white">
      <SEO
        title={null}
        description="Long established as one of Bermuda's most trusted practices for clinical massage therapy, Ashlan Clinic is distinguished by the experience of its Therapists."
        path="/"
      />
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="border-b border-[#e5e7eb] bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12 md:pb-16">
          <div className="max-w-4xl ash-fade-up">
            <span className="ash-eyebrow">Ashlan Clinic · Bermuda</span>
            <p className="mt-6 max-w-3xl text-lg md:text-xl text-[#3C4048] leading-relaxed">
              Long established as one of Bermuda's most trusted practices for
              clinical massage therapy,{" "}
              <span className="block mt-4 mb-4 text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight uppercase text-[#080808]">
                Ashlan Clinic is distinguished by the experience of its
                Therapists
              </span>
              the rigour of its clinical method, and a quiet, methodical focus
              on resolving the underlying cause of pain. Recognized across the
              island for evidence-based treatment and measurable outcomes.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={BOOKING_URL}
                data-testid="hero-book-button"
                className="ash-btn-primary"
              >
                Book Your Session
                <ArrowUpRight size={18} />
              </a>
              <Link
                to="/team"
                data-testid="hero-meet-team-button"
                className="ash-btn-secondary"
              >
                Meet Our Team
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
          <div className="aspect-[16/9] w-full overflow-hidden bg-[#F5F5F5]">
            <img
              src={ASSETS.heroClinical}
              alt="Clinical massage treatment at Ashlan Clinic"
              className="w-full h-full object-cover"
              data-testid="hero-image"
            />
          </div>
        </div>
      </section>

      {/* H4 — Trust counter */}
      <TrustCounter target={SESSIONS_DELIVERED} label="Sessions delivered" suffix="+" />

      {/* TRUST BAR */}
      <section
        data-testid="trust-bar"
        className="bg-[#F5F5F5] border-b border-[#e5e7eb]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Award, label: "Best of Bermuda Award Winner" },
            { icon: ShieldCheck, label: "Insurance Accepted" },
            { icon: Clock, label: "30+ Years Experience" },
            { icon: PackageCheck, label: "Official Therabody Reseller" },
          ].map((t, i) => {
            const Icon = t.icon;
            return (
              <div
                key={i}
                className="flex items-start gap-4"
                data-testid={`trust-item-${i}`}
              >
                <Icon size={22} strokeWidth={1.5} className="text-[#3A7A8C] mt-1" />
                <p className="text-[#080808] text-sm md:text-base font-semibold leading-snug">
                  {t.label}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section data-testid="what-we-do" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
            <div className="md:col-span-7">
              <span className="ash-eyebrow">What We Do</span>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-tight tracking-tight text-[#080808]">
                Clinical Massage, Transformative Experience, and Precise Tools.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-[#3C4048] text-lg leading-relaxed">
                Three integrated streams of care designed to identify the cause,
                deliver the treatment, and support recovery between sessions.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {WHAT_WE_DO.map((card, idx) => {
              const Icon = iconMap[card.icon] || Stethoscope;
              return (
                <Link
                  to={card.link}
                  key={idx}
                  data-testid={`what-we-do-card-${idx}`}
                  className="group bg-white border border-[#e5e7eb] p-10 flex flex-col gap-6 transition-transform duration-300 hover:-translate-y-1"
                >
                  <Icon size={28} strokeWidth={1.4} className="text-[#3A7A8C]" />
                  <h3 className="text-2xl font-bold text-[#080808] leading-tight">
                    {card.title}
                  </h3>
                  <p className="text-[#3C4048] leading-relaxed">{card.body}</p>
                  <span className="ash-link-underline mt-auto">
                    Learn More <ArrowRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY ASHLAN */}
      <section
        data-testid="why-ashlan"
        className="bg-[#F5F5F5] border-y border-[#e5e7eb] py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-7">
            <span className="ash-eyebrow">Why Ashlan</span>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight text-[#080808]">
              We Don’t Treat Symptoms. We Resolve Them.
            </h2>
            <p className="mt-6 text-lg text-[#3C4048] max-w-2xl leading-relaxed">
              Our therapists work through a clear, repeatable clinical process —
              one that identifies the underlying driver of your pain and builds
              a treatment plan around resolving it.
            </p>

            <ol className="mt-12 grid sm:grid-cols-2 gap-6">
              {PROCESS_STEPS.map((s) => (
                <li
                  key={s.n}
                  data-testid={`process-step-${s.n}`}
                  className="bg-white p-6 border-l-2 border-[#5FA8B4]"
                >
                  <div className="text-xs font-bold text-[#3A7A8C] tracking-[0.22em]">
                    {s.n}
                  </div>
                  <h4 className="mt-2 text-lg font-bold text-[#080808]">
                    {s.t}
                  </h4>
                  <p className="mt-2 text-sm text-[#3C4048] leading-relaxed">
                    {s.d}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={ASSETS.cupping}
                alt="Cupping treatment at Ashlan Clinic"
                className="w-full h-[340px] md:h-[520px] object-cover"
                data-testid="why-ashlan-image"
              />
              <div className="absolute -bottom-6 -left-6 bg-white border border-[#e5e7eb] p-6 max-w-xs hidden md:block">
                <div className="ash-eyebrow">Outcome-led</div>
                <p className="mt-2 text-[#080808] font-bold leading-snug">
                  Every plan is measured against a clinical baseline.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT BRINGS YOU IN */}
      <section
        data-testid="what-brings-you-in"
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="ash-eyebrow">Conditions Treated</span>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#080808]">
                What Brings You In?
              </h2>
            </div>
            <Link
              to="/services"
              className="ash-link-underline"
              data-testid="conditions-services-link"
            >
              See all services <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {CONDITIONS.map((c, i) => {
              const Icon = iconMap[c.icon] || Activity;
              return (
                <div
                  key={i}
                  data-testid={`condition-card-${i}`}
                  className="bg-[#F5F5F5] border-l-2 border-[#5FA8B4] p-6 md:p-8 transition-colors hover:bg-white hover:border-[#3A7A8C]"
                >
                  <Icon
                    size={24}
                    strokeWidth={1.4}
                    className="text-[#3A7A8C] mb-4"
                  />
                  <h3 className="text-lg md:text-xl font-bold text-[#080808] leading-tight">
                    {c.name}
                  </h3>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TEAM PREVIEW */}
      <section
        data-testid="team-preview"
        className="py-24 md:py-32 bg-[#F5F5F5] border-y border-[#e5e7eb]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-8">
              <span className="ash-eyebrow">Our Therapists</span>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#080808]">
                One Vision. One Clinical Standard.
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to="/team"
                className="ash-link-underline"
                data-testid="team-preview-link"
              >
                Meet the team <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {THERAPISTS.map((t, i) => (
              <Link
                to="/team"
                key={t.slug}
                data-testid={`team-preview-card-${i}`}
                className="group bg-white border border-[#e5e7eb] flex flex-col transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="aspect-square sm:aspect-[4/5] w-full max-h-[420px] overflow-hidden bg-[#F5F5F5] flex items-center justify-center">
                  <img
                    src={t.photo}
                    alt={`${t.name}, ${t.role}`}
                    className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-[#080808] leading-tight">
                    {t.name}{" "}
                    <span className="text-[#3A7A8C] font-semibold text-base ml-1">
                      {t.credentials}
                    </span>
                  </h3>
                  <p className="mt-3 text-[#3C4048]">{t.role}</p>
                  <span className="mt-6 ash-link-underline">
                    View Profile <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section
        data-testid="testimonials-section"
        className="py-24 md:py-32 bg-white"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-8">
              <span className="ash-eyebrow">Client Outcomes</span>
              <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-[#080808]">
                Words from the table.
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <TestimonialModal
                trigger={
                  <button
                    type="button"
                    className="ash-btn-secondary"
                    data-testid="share-experience-button"
                  >
                    Share Your Experience
                    <ArrowUpRight size={16} />
                  </button>
                }
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.slice(0, 3).map((t, i) => (
              <figure
                key={t.id || i}
                data-testid={`testimonial-card-${i}`}
                className="bg-[#F5F5F5] p-10 flex flex-col gap-6"
              >
                <Quote
                  size={26}
                  strokeWidth={1.4}
                  className="text-[#5FA8B4]"
                />
                <blockquote className="text-[#080808] text-lg leading-relaxed font-medium">
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-auto">
                  <div className="text-[#080808] font-bold">{t.name}</div>
                  {t.location && (
                    <div className="text-sm text-[#6B7280] uppercase tracking-[0.18em]">
                      {t.location}
                    </div>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* THERABODY */}
      <section
        data-testid="therabody-section"
        className="bg-[#1A1A1A] text-white py-24 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col items-center text-center gap-6 mb-16">
            <img
              src={ASSETS.therabodyLogo}
              alt="Therabody"
              className="h-10 object-contain invert opacity-90"
            />
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#9DD4D8]">
              Official Bermuda Reseller
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white max-w-3xl leading-tight">
              Professional recovery technology, prescribed by our therapists.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {THERABODY_PRODUCTS.slice(0, 3).map((p, i) => (
              <div
                key={p.slug}
                data-testid={`therabody-product-${i}`}
                className="bg-[#0F0F0F] border border-[#262626] flex flex-col"
              >
                <div className="aspect-[4/3] overflow-hidden bg-[#1A1A1A]">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-bold text-white leading-tight">
                    {p.name}
                  </h3>
                  <p className="mt-3 text-[#A3A3A3] leading-relaxed text-sm">
                    {p.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 flex justify-center">
            <Link
              to="/therabody"
              data-testid="therabody-view-products-button"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-[#080808] font-bold uppercase tracking-[0.14em] text-xs border border-white hover:bg-[#9DD4D8] hover:border-[#9DD4D8] transition-colors"
            >
              View Therabody Products
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CARE THAT DOESN'T CLOCK OUT — modern digital care system */}
      <section
        data-testid="care-247-section"
        className="bg-white py-24 md:py-32 border-b border-[#e5e7eb]"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="ash-eyebrow" data-testid="care-247-eyebrow">
              Always On
            </span>
            <h2
              data-testid="care-247-heading"
              className="mt-4 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#080808] leading-[1.05]"
            >
              Care that doesn&apos;t clock out.
            </h2>
            <p
              data-testid="care-247-intro"
              className="mt-6 text-lg text-[#3C4048] leading-relaxed"
            >
              Great treatment shouldn&apos;t start with a game of phone tag.
              Behind every visit, Ashlan runs on a modern digital system built
              to make your care effortless — from your very first question to
              your next appointment.
            </p>
          </div>

          <div className="mt-14 md:mt-20 grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: MessageCircle,
                title: "Answers, any hour",
                body:
                  "Our AI concierge helps you find the right therapy and answers your questions day or night — so you're never left waiting for the clinic to open.",
                testid: "care-247-col-1",
              },
              {
                icon: PhoneCall,
                title: "Book by phone, without the hold music",
                body:
                  "Call us and our AI receptionist can help you book on the spot — getting you seen sooner, with no queues and no callbacks.",
                testid: "care-247-col-2",
              },
              {
                icon: BookOpen,
                title: "Guidance that meets you where you are",
                body:
                  "Clear, trustworthy resources on your condition and recovery — so you arrive informed and leave with a plan.",
                testid: "care-247-col-3",
              },
            ].map((col) => (
              <div
                key={col.title}
                data-testid={col.testid}
                className="bg-[#F5F5F5] p-8 md:p-10 flex flex-col border-l-2 border-[#5FA8B4]"
              >
                <col.icon
                  size={28}
                  strokeWidth={1.5}
                  className="text-[#3A7A8C]"
                />
                <h3 className="mt-5 text-xl md:text-2xl font-bold text-[#080808] leading-snug">
                  {col.title}
                </h3>
                <p className="mt-4 text-[#3C4048] leading-relaxed text-base">
                  {col.body}
                </p>
              </div>
            ))}
          </div>

          <p
            data-testid="care-247-closing"
            className="mt-16 md:mt-20 text-center italic text-lg text-[#3C4048] max-w-2xl mx-auto"
          >
            A better health experience, powered by smart systems.
          </p>

          <div className="mt-10 flex justify-center">
            <a
              href="https://partner-uk.pabau.com/online-bookings/ashlanclinic"
              data-testid="care-247-book-button"
              className="ash-btn-primary"
            >
              Book Your Session
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
